import { useCallback, useSyncExternalStore } from 'react';
import {
  getYbotDockState,
  LSKey,
  notifyLocalStoreKeyUpdated,
  setYbotDockState,
  subscribeToLocalStoreKey,
} from '../../../store/localStore';
import { useSelectedProjectShortName } from '../../../hooks/useSelectedProjectShortName';
import { useUser } from '../../../hooks/useUser';
import { useGetYbotAvailabilityQuery } from './apiYbot';

export const YBOT_DOCK_DEFAULT_HEIGHT = 360;
export const YBOT_DOCK_MIN_HEIGHT = 200;
export const YBOT_DOCK_MAX_HEIGHT = 900;

export const clampYbotDockHeight = (height: number): number => {
  if (!Number.isFinite(height)) {
    return YBOT_DOCK_DEFAULT_HEIGHT;
  }

  return Math.min(YBOT_DOCK_MAX_HEIGHT, Math.max(YBOT_DOCK_MIN_HEIGHT, Math.round(height)));
};

export interface YbotDockControls {
  /** True only once the server has confirmed it. While the answer is in flight this is false. */
  isAvailable: boolean;
  isOpen: boolean;
  height: number;
  /** The server's own hung-turn ceiling; undefined until availability has answered. */
  turnTimeoutSeconds: number | undefined;
  open: () => void;
  close: () => void;
  setHeight: (height: number) => void;
}

/**
 * `isOpen` and `height` live in localStorage, deliberately not in Redux restored on mount:
 * `resetAllSlices()` runs in a parent route and child effects run first, so the reset would close
 * a dock a restore effect had just opened. Availability wins over the stored value.
 */
export const useYbotDock = (): YbotDockControls => {
  const { data: availability } = useGetYbotAvailabilityQuery(undefined);
  const projectShortName = useSelectedProjectShortName();
  const userId = useUser()?.id;

  const isAvailable = availability?.enabled === true;

  const subscribe = useCallback(
    (listener: () => void) => subscribeToLocalStoreKey(LSKey.YBOT_DOCK_STATE, listener),
    [],
  );

  // Primitive snapshots: useSyncExternalStore compares by identity, and parsed JSON is a new
  // object every render.
  const getStoredOpen = useCallback(
    (): boolean => getYbotDockState(projectShortName, userId)?.isOpen ?? false,
    [projectShortName, userId],
  );

  const getStoredHeight = useCallback((): number => {
    const stored = getYbotDockState(projectShortName, userId)?.height;
    return stored === undefined ? YBOT_DOCK_DEFAULT_HEIGHT : clampYbotDockHeight(stored);
  }, [projectShortName, userId]);

  const storedOpen = useSyncExternalStore(subscribe, getStoredOpen, () => false);
  const height = useSyncExternalStore(subscribe, getStoredHeight, () => YBOT_DOCK_DEFAULT_HEIGHT);

  const write = useCallback(
    (state: { isOpen: boolean; height: number }): void => {
      setYbotDockState(projectShortName, userId, {
        isOpen: state.isOpen,
        height: clampYbotDockHeight(state.height),
      });
      notifyLocalStoreKeyUpdated(LSKey.YBOT_DOCK_STATE);
    },
    [projectShortName, userId],
  );

  const open = useCallback(() => write({ isOpen: true, height }), [height, write]);
  const close = useCallback(() => write({ isOpen: false, height }), [height, write]);
  const setHeight = useCallback((next: number) => write({ isOpen: true, height: next }), [write]);

  return {
    isAvailable,
    isOpen: isAvailable && storedOpen,
    height,
    turnTimeoutSeconds: availability?.turnTimeoutSeconds,
    open,
    close,
    setHeight,
  };
};
